import { useCallback, useEffect, useRef, useState } from 'react'
import type { PublicDeskProjectionV1 } from '@shared/publicDesk'

// Live desk data: fetch the projection, then follow revision notifications.
//
// The stream deliberately carries only a revision number, so every actual read
// goes back through the normal HTTP path where revocation and expiry are
// enforced. That also makes reconnection trivial: on reconnect we re-read, and
// if nothing changed the server says so without resending the desk.
//
// The connection state is reported honestly rather than optimistically. A
// viewer looking at a desk that stopped updating twenty minutes ago should be
// told, not left to assume it is current.

export type LiveState =
  | 'loading'
  | 'live'
  | 'reconnecting'
  | 'paused'
  | 'revoked'
  | 'pending'
  | 'error'

export interface LiveDesk {
  state: LiveState
  projection: PublicDeskProjectionV1 | null
  revision: number
  publishedAt: number | null
  error: string | null
  /** True once a revision has been shown and the stream later dropped. */
  stale: boolean
  refresh: () => void
}

interface DeskResponse {
  ok: boolean
  state?: 'ready' | 'current' | 'pending'
  revision?: number
  paused?: boolean
  publishedAt?: number
  projection?: PublicDeskProjectionV1
  error?: string
}

export function useLiveDesk(apiBase: string, token: string | null): LiveDesk {
  const [state, setState] = useState<LiveState>('loading')
  const [projection, setProjection] = useState<PublicDeskProjectionV1 | null>(null)
  const [revision, setRevision] = useState(0)
  const [publishedAt, setPublishedAt] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  // Read through a ref so the fetcher is stable and the SSE effect does not
  // tear down and rebuild the stream on every revision.
  const revisionRef = useRef(0)
  const haveContent = useRef(false)

  const load = useCallback(
    async (after?: number) => {
      if (!token) return
      try {
        const q = after != null ? `?after=${after}` : ''
        const res = await fetch(`${apiBase}/share/${token}/desk${q}`)
        if (res.status === 404) {
          setState('revoked')
          return
        }
        if (!res.ok) {
          setError(`The server returned ${res.status}.`)
          setState(haveContent.current ? 'reconnecting' : 'error')
          return
        }
        const body = (await res.json()) as DeskResponse
        if (!body.ok) {
          setState('revoked')
          return
        }
        if (body.state === 'pending') {
          setState('pending')
          return
        }
        if (typeof body.revision === 'number') {
          revisionRef.current = body.revision
          setRevision(body.revision)
        }
        if (body.state === 'ready' && body.projection) {
          setProjection(body.projection)
          setPublishedAt(body.publishedAt ?? null)
          haveContent.current = true
        }
        setError(null)
        setStale(false)
        setState(body.paused ? 'paused' : 'live')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not reach the server.')
        // Keep showing the last good revision rather than blanking the desk.
        setState(haveContent.current ? 'reconnecting' : 'error')
      }
    },
    [apiBase, token]
  )

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!token) return
    let closed = false
    let es: EventSource | null = null
    let retry: ReturnType<typeof setTimeout> | null = null
    let backoff = 1000

    const connect = (): void => {
      if (closed) return
      es = new EventSource(`${apiBase}/share/${token}/events`)
      es.onopen = () => {
        backoff = 1000
        // A stream that just opened may have missed revisions while it was
        // down, so reconcile against the server rather than assuming.
        void load(revisionRef.current)
      }
      es.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data) as { revision: number }
          if (msg.revision === -1) {
            setState('revoked')
            setProjection(null)
            es?.close()
            closed = true
            return
          }
          if (msg.revision !== revisionRef.current) void load(revisionRef.current)
        } catch {
          // A malformed frame is not worth tearing the stream down for.
        }
      }
      es.onerror = () => {
        es?.close()
        if (closed) return
        // Showing the last good revision is right, but saying it is live is not.
        if (haveContent.current) {
          setStale(true)
          setState('reconnecting')
        }
        retry = setTimeout(connect, backoff)
        backoff = Math.min(backoff * 2, 30_000)
      }
    }
    connect()

    return () => {
      closed = true
      if (retry) clearTimeout(retry)
      es?.close()
    }
  }, [apiBase, token, load])

  return {
    state,
    projection,
    revision,
    publishedAt,
    error,
    stale,
    refresh: () => void load()
  }
}
